// The one owner of the web unread count and SSE connection. Each useNotifications list applies
// its events, so the bell and the Activity view agree.

import type { Notification } from '@parlay-picker/shared/notification';
import { readUnreadTotal } from '@parlay-picker/shared/notificationUnreadTotal';
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from './AuthContext';

// =============================================================================
// Types
// =============================================================================

/** A change every list applies to the rows it holds */
export type NotificationsEvent =
  | { kind: 'arrived'; notification: Notification }
  | { kind: 'read'; ids: string[]; readAt: string }
  | { kind: 'readAll'; readAt: string }
  | { kind: 'dismissed'; id: string };

type NotificationsListener = (event: NotificationsEvent) => void;

interface NotificationsOwner {
  unreadCount: number;
  isConnected: boolean;
  /** countUnread reads the caller's rows once the server agrees, so a repeated mark counts once */
  markAsRead: (ids: string[], countUnread: () => number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (id: string, wasUnread: () => boolean) => Promise<void>;
  /** Sets the count to the server's unread total */
  refreshUnreadCount: () => Promise<void>;
  subscribe: (listener: NotificationsListener) => () => void;
}

interface SSEMessage {
  type: 'connected' | 'heartbeat' | 'notification';
  clientId?: string;
  timestamp?: number;
  notification?: Notification;
}

const NotificationsContext = createContext<NotificationsOwner | null>(null);

// =============================================================================
// Provider
// =============================================================================

export function NotificationsProvider({ children }: { children: ReactNode }): ReactElement {
  const { authFetch, user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const listenersRef = useRef(new Set<NotificationsListener>());
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Exponential backoff state for SSE reconnection */
  const reconnectDelayRef = useRef(5000);
  const consecutiveFailuresRef = useRef(0);
  const countVersionRef = useRef(0);

  // Every local count change goes through here, so a re-read in flight across it is not applied
  const changeCount = useCallback((update: (count: number) => number) => {
    countVersionRef.current++;
    setUnreadCount(update);
  }, []);

  const subscribe = useCallback((listener: NotificationsListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // Called in the same handler as the count change, so React renders the badge and the rows together
  const emit = useCallback((event: NotificationsEvent) => {
    for (const listener of listenersRef.current) listener(event);
  }, []);

  // ─── Unread total (lightweight) ──────────────────────────────────────
  const refreshUnreadCount = useCallback(async () => {
    const total = await readUnreadTotal(
      () => authFetch(`${API_BASE_URL}/api/notifications/unread-count`),
      () => countVersionRef.current,
    );
    if (total !== null) setUnreadCount(total);
  }, [authFetch]);

  // ─── Mark as read ────────────────────────────────────────────────────
  const markAsRead = useCallback(
    async (ids: string[], countUnread: () => number) => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/notifications/mark-read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids }),
        });
        if (res.ok) {
          const unreadMarked = countUnread();
          changeCount((c) => Math.max(0, c - unreadMarked));
          emit({ kind: 'read', ids, readAt: new Date().toISOString() });
          // A read that saw the write and landed first has counted it too, so re-read the total
          await refreshUnreadCount();
        }
      } catch {
        // Silently fail
      }
    },
    [authFetch, emit, changeCount, refreshUnreadCount],
  );

  // ─── Mark all as read ────────────────────────────────────────────────
  const markAllAsRead = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        changeCount(() => 0);
        emit({ kind: 'readAll', readAt: new Date().toISOString() });
        await refreshUnreadCount();
      }
    } catch {
      // Silently fail
    }
  }, [authFetch, emit, changeCount, refreshUnreadCount]);

  // ─── Dismiss ─────────────────────────────────────────────────────────
  const dismiss = useCallback(
    async (id: string, wasUnread: () => boolean) => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/notifications/${id}`, {
          method: 'DELETE',
        });
        if (res.ok) {
          if (wasUnread()) changeCount((c) => Math.max(0, c - 1));
          emit({ kind: 'dismissed', id });
          await refreshUnreadCount();
        }
      } catch {
        // Silently fail
      }
    },
    [authFetch, emit, changeCount, refreshUnreadCount],
  );

  // ─── SSE Connection Setup ───────────────────────────────────────────
  const MAX_RECONNECT_DELAY = 60_000; // 60s cap
  const MAX_CONSECUTIVE_FAILURES = 5; // Stop after 5 failures: 5 + 10 + 20 + 40 s of waits between them

  const connect = useCallback(() => {
    if (!user) return;

    // Give up after too many consecutive failures (e.g., during extended outage)
    if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) return;

    // Clean up any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource(`${API_BASE_URL}/api/notifications/subscribe`);

    eventSource.onopen = () => {
      setIsConnected(true);
      // Reset backoff on successful connection
      reconnectDelayRef.current = 5000;
      consecutiveFailuresRef.current = 0;
    };

    eventSource.onmessage = (event) => {
      try {
        const data: SSEMessage = JSON.parse(event.data);

        // Ignore heartbeat and connection messages
        if (data.type === 'heartbeat' || data.type === 'connected') return;

        // Handle new notification
        if (data.type === 'notification' && data.notification) {
          const newNotification = data.notification;

          if (!newNotification.readAt) {
            changeCount((prev) => prev + 1);
            // A read that saw it stored and landed before this event has counted it too
            refreshUnreadCount();
          }
          emit({ kind: 'arrived', notification: newNotification });

          // Dispatch custom event for parlay share notifications so
          // App's received shares can refresh without polling
          if (newNotification.type === 'parlay_shared_with_you') {
            window.dispatchEvent(new Event('parlay-shared-with-you'));
          }
        }
      } catch (_err) {}
    };

    eventSource.onerror = () => {
      setIsConnected(false);
      eventSource.close();

      consecutiveFailuresRef.current++;

      // Stop reconnecting after too many failures
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) return;

      // Exponential backoff: 5s → 10s → 20s → 40s → 60s (cap)
      const delay = reconnectDelayRef.current;
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY);
    };

    eventSourceRef.current = eventSource;
  }, [user, emit, changeCount, refreshUnreadCount]);

  // ─── Connect on mount, disconnect on unmount ─────────────────────────
  useEffect(() => {
    if (!user) return;

    // Initial fetch
    refreshUnreadCount();

    // Establish SSE connection
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [user, connect, refreshUnreadCount]);

  // ─── Pause connection when tab is hidden ─────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden: close connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setIsConnected(false);
      } else {
        // Tab visible again: reset backoff and reconnect
        reconnectDelayRef.current = 5000;
        consecutiveFailuresRef.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect]);

  const owner = useMemo(
    (): NotificationsOwner => ({
      unreadCount,
      isConnected,
      markAsRead,
      markAllAsRead,
      dismiss,
      refreshUnreadCount,
      subscribe,
    }),
    [unreadCount, isConnected, markAsRead, markAllAsRead, dismiss, refreshUnreadCount, subscribe],
  );

  return <NotificationsContext.Provider value={owner}>{children}</NotificationsContext.Provider>;
}

export function useNotificationsOwner(): NotificationsOwner {
  const owner = useContext(NotificationsContext);
  if (!owner) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return owner;
}
